export const DEFAULT_COLOR_PALETTE: string[] = [
  '#4B9CD5',
  '#1B338C',
  '#A7D4EE',
  '#579A42',
  '#AAFCBA',
  '#3A5F71',
  '#EFE6D6',
  '#5BBEBA',
  '#FBF486',
  '#A4A9E2',
  '#D7423B',
  '#CCCBC9',
  '#A96C20',
  '#968F87',
];

export const UNMAPPED_COLOR = '#000000';

export const withAlpha = (hex: string, alpha: number): string => {
  const hexValue = hex.replace('#', '');
  const r = parseInt(hexValue.substring(0, 2), 16);
  const g = parseInt(hexValue.substring(2, 4), 16);
  const b = parseInt(hexValue.substring(4, 6), 16);

  return `rgba(${r},${g},${b},${alpha})`;
};

export const getPaletteColorOrFallback = (
  index: number,
  colorPalette?: string[],
): string => {
  const colors = colorPalette || DEFAULT_COLOR_PALETTE;

  if (index < 0) {
    return UNMAPPED_COLOR;
  }

  if (index >= colors.length) {
    const baseColor = colors[index % colors.length];

    return withAlpha(baseColor, 0.4);
  }

  return colors[index];
};

export const buildStatusColorPalette = (
  statuses: string[],
  statusColors: Record<string, string>,
  fallbackPalette: string[] = DEFAULT_COLOR_PALETTE,
): string[] => {
  let fallbackIndex = 0;

  return statuses.map((status) => {
    const mappedColor = statusColors[status];
    if (mappedColor) return mappedColor;

    const paletteLength = fallbackPalette.length;

    if (fallbackIndex < paletteLength) {
      const fallbackColor = fallbackPalette[fallbackIndex];
      fallbackIndex += 1;

      return fallbackColor;
    }
    const baseColor = fallbackPalette[fallbackIndex % paletteLength];

    return withAlpha(baseColor, 0.4);
  });
};

// Picks black or white text so it stays legible against any bar/cell
// background color — same YIQ threshold used for tree table category cells.
export const getContrastingTextColor = (backgroundColor: string): string => {
  const hex = backgroundColor.replace('#', '');

  if (hex.length !== 6) {
    return '#000000';
  }

  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);
  const yiq = (r * 299 + g * 587 + b * 114) / 1000;

  return yiq < 128 ? '#ffffff' : '#000000';
};

// A dimmed (unselected) bar renders at ~20% opacity over a white card
// background; blend before checking contrast so the label color matches
// what's actually visible instead of the fully-opaque base color.
export const blendWithWhite = (hexColor: string, alpha: number): string => {
  const hex = hexColor.replace('#', '');

  if (hex.length !== 6) {
    return hexColor;
  }

  const toHex = (channel: number): string =>
    Math.round(channel * alpha + 255 * (1 - alpha))
      .toString(16)
      .padStart(2, '0');
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);

  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
};
